"use client";

import type { Frame } from "@/lib/tool-kit/stats-distributions/chart";
import type { HistogramGeometry } from "@/lib/tool-kit/stats-distributions-more/clt-chart";

/*
 * The simulator's histogram of sample means with the normal overlay. Geometry comes from
 * `clt-chart.ts`; the label comes in as a prop from the widget's own strings. Colours
 * are theme utilities, so the drawing follows light and dark mode.
 */

function percentX(position: number, frame: Frame): string {
  return `${(position / frame.width) * 100}%`;
}

export function SamplingHistogram({
  geometry,
  label,
  format,
}: {
  geometry: HistogramGeometry;
  /** Read by screen readers in place of the picture. */
  label: string;
  format: (value: number) => string;
}) {
  const { frame, baseline } = geometry;
  return (
    <div>
      <svg
        aria-label={label}
        className="block h-auto w-full"
        role="img"
        viewBox={`0 0 ${frame.width} ${frame.height}`}
      >
        {geometry.bars.map((bar) => (
          <rect
            className="fill-primary/40"
            height={bar.height}
            key={bar.index}
            width={bar.width}
            x={bar.x}
            y={bar.y}
          />
        ))}
        <path
          className="stroke-foreground"
          d={geometry.curve}
          fill="none"
          strokeLinejoin="round"
          strokeWidth={2}
        />
        <line
          className="stroke-muted-foreground"
          strokeWidth={1}
          x1={frame.left}
          x2={frame.width - frame.right}
          y1={baseline}
          y2={baseline}
        />
        {geometry.ticks.map((tick) => (
          <line
            className="stroke-muted-foreground"
            key={tick.value}
            x1={tick.position}
            x2={tick.position}
            y1={baseline}
            y2={baseline + 5}
          />
        ))}
      </svg>
      <div
        aria-hidden="true"
        className="relative h-5 text-muted-foreground text-xs"
      >
        {geometry.ticks.map((tick) => (
          <span
            className="absolute top-0 -translate-x-1/2 whitespace-nowrap tabular-nums"
            key={tick.value}
            style={{ left: percentX(tick.position, frame) }}
          >
            {format(tick.value)}
          </span>
        ))}
      </div>
    </div>
  );
}
